import React from 'react'
import { FiGrid, FiBriefcase } from 'react-icons/fi'
import { Link } from 'react-router-dom'
import { GoGear } from 'react-icons/go'
import {
  PiEnvelopeSimple,
  PiHardDrives,
  PiLightningA,
  PiTreeStructure,
  PiUsersThree,
} from 'react-icons/pi'
import { VscBell } from 'react-icons/vsc'

import styled from 'styled-components'

import { User } from 'models/user'
import { FeatureFlags, featureFlagService } from 'utils/featureFlagService'
import { useAuth } from 'context/authentication/useAuth'
import { useSideMenu } from '../context/SideMenuContext'

const icons: Record<TabGroupKeys, React.ReactNode> = {
  company: <FiBriefcase size={24} />,
  users: <PiUsersThree size={24} />,
  platform: <PiHardDrives size={24} />,
  data: <PiTreeStructure size={24} />,
  misc: <GoGear size={24} />,
  notifications: <VscBell size={24} />,
  automations: <PiLightningA size={24} />,
  'email-outreach': <PiEnvelopeSimple size={24} />,
}

export function SideMenu({ selectedGroup }: { selectedGroup: TabGroupKeys }) {
  const featureFlags = featureFlagService()
  const { user } = useAuth()
  const { tabGroups } = useSideMenu()

  return (
    <SideMenuContainer>
      {Object.entries(tabGroups).map(([key, group]) => {
        const firstTab = Object.values(group.tabs)[0]

        return (
          <React.Fragment key={key}>
            {group.isAllowed(user, featureFlags) ? (
              <MenuItem
                key={key}
                selected={selectedGroup === key}
                to={`/settings/${group.uri}/${firstTab?.uri}`}
              >
                {icons[key as TabGroupKeys] || <FiGrid size={20} />}{' '}
                {group.title}
              </MenuItem>
            ) : null}
          </React.Fragment>
        )
      })}
    </SideMenuContainer>
  )
}

const SideMenuContainer = styled.div`
  width: 100%;
  min-width: 220px;
  max-width: 220px;
  background: #e6f1ec;
  padding: 16px 16px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const MenuItem = styled(Link)<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  width: 100%;
  background: ${({ selected }) => (selected ? '#C5DED3' : 'transparent')};
  border-radius: 8px;
  font-weight: ${({ selected }) => (selected ? 'bold' : 'normal')};
  color: #191d23;
  font-size: 16px;
  transition: background 0.2s;
  &:hover {
    background: #d4e7de;
  }
  &:active {
    background: #c5ded3;
  }
`

export interface Tab {
  uri: string
  title: string
  isAllowed: (user?: User, featureFlags?: FeatureFlags) => boolean
}

export type TabGroupKeys =
  | 'users'
  | 'platform'
  | 'data'
  | 'company'
  | 'misc'
  | 'notifications'
  | 'automations'
  | 'email-outreach'

export type TabKeys =
  | 'users'
  | 'products'
  | 'sales-stages'
  | 'filters'
  | 'columns'
  | 'product-tags'
  | 'distributors'
  | 'gpos'
  | 'contact-tags'
  | 'document-tags'
  | 'email-integration'
  | 'misc'
  | 'account'
  | 'domains'
  | 'platform'
  | 'deals'
  | 'contacts'
  | 'campaigns'
  | 'rebates'
  | 'automations'
  | 'logo'
  | 'sender-emails'

export interface TabGroup extends Tab {
  tabs: {
    [key in TabKeys]?: Tab
  }
}

export type TabGroups = {
  [key in TabGroupKeys]: TabGroup
}

export interface DataTabCounts {
  filters: number | null
  productTags: number | null
  contactTags: number | null
  documentTags: number | null
  columnPresets: number | null
}

const formatCount = (count: number | null | undefined): string =>
  count === null || count === undefined ? '' : ` (${count})`

export function createTabGroups(
  logoCount: number = 0,
  dataTabCounts?: DataTabCounts
): TabGroups {
  return {
    company: {
      title: 'Company',
      uri: 'company',
      isAllowed(user, featureFlag) {
        return Object.values(this.tabs).some(
          (tab) => tab && tab.isAllowed(user, featureFlag)
        )
      },
      tabs: {
        account: {
          title: 'Account',
          uri: 'account',
          isAllowed: (user?: User) => !!user?.is_billing_admin,
        },
        logo: {
          title: `Logo (${logoCount})`,
          uri: 'logo',
          isAllowed: (user?: User) => !!user?.is_billing_admin,
        },
        domains: {
          title: 'Domains',
          uri: 'domains',
          isAllowed: (user?: User, featureFlags?: FeatureFlags) =>
            !!user?.is_billing_admin &&
            !!featureFlags?.enableCompanyEmailExtensionSettings,
        },
      },
    },
    users: {
      title: 'Users',
      uri: 'users',
      isAllowed(user, featureFlag) {
        return Object.values(this.tabs).some(
          (tab) => tab && tab.isAllowed(user, featureFlag)
        )
      },
      tabs: {
        users: {
          title: 'Users',
          uri: 'users',
          isAllowed: () => true,
        },
        'email-integration': {
          title: 'Email Integration',
          uri: 'email-integration',
          isAllowed: (_user, featureFlags?: FeatureFlags) =>
            !!featureFlags?.enableEmailIntegration,
        },
      },
    },
    platform: {
      title: 'Platform',
      uri: 'platform',
      isAllowed(user, featureFlag) {
        return Object.values(this.tabs).some(
          (tab) => tab && tab.isAllowed(user, featureFlag)
        )
      },
      tabs: {
        products: {
          title: 'Products',
          uri: 'products',
          isAllowed: (_user, featureFlags?: FeatureFlags) =>
            !featureFlags?.enableNewNavigation,
        },
        'sales-stages': {
          title: 'Sales Stages',
          uri: 'sales-stages',
          isAllowed: () => true,
        },
        distributors: {
          title: 'Distributors',
          uri: 'distributors',
          isAllowed: () => true,
        },
        gpos: {
          title: 'GPOs',
          uri: 'gpos',
          isAllowed: () => true,
        },
      },
    },
    data: {
      title: 'Data',
      uri: 'data',
      isAllowed(user, featureFlag) {
        return Object.values(this.tabs).some(
          (tab) => tab && tab.isAllowed(user, featureFlag)
        )
      },
      tabs: {
        filters: {
          title: `Filters${formatCount(dataTabCounts?.filters)}`,
          uri: 'filters',
          isAllowed: () => true,
        },
        'product-tags': {
          title: `Tags${formatCount(dataTabCounts?.productTags)}`,
          uri: 'product-tags',
          isAllowed: () => true,
        },
        'contact-tags': {
          title: `Contact Labels${formatCount(dataTabCounts?.contactTags)}`,
          uri: 'contact-tags',
          isAllowed: () => true,
        },
        'document-tags': {
          title: `Document Tags${formatCount(dataTabCounts?.documentTags)}`,
          uri: 'document-tags',
          isAllowed: () => true,
        },
        columns: {
          title: `Column Presets${formatCount(dataTabCounts?.columnPresets)}`,
          uri: 'columns',
          isAllowed: () => true,
        },
      },
    },
    notifications: {
      title: 'Notifications',
      uri: 'notifications',
      isAllowed(user, featureFlag) {
        const atLeastOneAllowed = Object.values(this.tabs).some(
          (tab) => tab && tab.isAllowed(user, featureFlag)
        )
        return (
          !!featureFlag?.enableUserNotificationPreferences && atLeastOneAllowed
        )
      },
      tabs: {
        platform: {
          title: 'Platform',
          uri: 'platform',
          isAllowed: (_, featureFlags) =>
            !!featureFlags?.enableUserNotificationPreferences,
        },
        deals: {
          title: 'Deals',
          uri: 'deals',
          isAllowed: (_, featureFlags) =>
            !!featureFlags?.enableUserNotificationPreferences,
        },
        contacts: {
          title: 'Contacts',
          uri: 'contacts',
          isAllowed: (_, featureFlags) =>
            !!featureFlags?.enableUserNotificationPreferences,
        },
        campaigns: {
          title: 'Campaigns',
          uri: 'campaigns',
          isAllowed: (_, featureFlags) =>
            !!featureFlags?.enableUserNotificationPreferences,
        },
        rebates: {
          title: 'Rebates',
          uri: 'rebates',
          isAllowed: (_, featureFlags) =>
            !!featureFlags?.enableUserNotificationPreferences,
        },
      },
    },
    misc: {
      title: 'Misc Settings',
      uri: 'misc',
      isAllowed(user, featureFlag) {
        return Object.values(this.tabs).some(
          (tab) => tab && tab.isAllowed(user, featureFlag)
        )
      },
      tabs: {
        misc: {
          title: 'Misc Settings',
          uri: 'misc',
          isAllowed: () => true,
        },
      },
    },
    automations: {
      title: 'Automations',
      uri: 'automations',
      isAllowed(user, featureFlag) {
        return Object.values(this.tabs).some(
          (tab) => tab && tab.isAllowed(user, featureFlag)
        )
      },
      tabs: {
        automations: {
          title: 'Automations',
          uri: 'automations',
          isAllowed: (_user, featureFlags?: FeatureFlags) =>
            !!featureFlags?.automationsPageEnabled,
        },
      },
    },
    'email-outreach': {
      title: 'Email Outreach',
      uri: 'email-outreach',
      isAllowed(user, featureFlag) {
        return Object.values(this.tabs).some(
          (tab) => tab && tab.isAllowed(user, featureFlag)
        )
      },
      tabs: {
        'sender-emails': {
          title: 'Sender Emails',
          uri: 'sender-emails',
          isAllowed: (user?: User, featureFlags?: FeatureFlags) =>
            !!featureFlags?.enableEmailSequencing &&
            !!user?.company?.has_bison_workspace,
        },
      },
    },
  }
}
